import { BookingAction } from './booking-action';
import { BookingParams, BookingPosition, BookingResult } from '../types';

/**
 * Geteilter Kern aller Buchungen mit Quelle = Maschine. Hält die welt-uniforme
 * Richtung Maschine → BufferstockDisposal (MachineTrashMovement). Welt-eigene
 * Richtungen leben in der jeweiligen Subklasse.
 */
export abstract class FromMachineActionBase extends BookingAction {
  /**
   * Maschine zum Disposal-Anker am Bufferstock. Erzeugt ein
   * `MachineTrashMovement`. Backend-Schema invertiert source/target (source =
   * Bufferstock, target = Maschine); hier laufen `sourceId` (Maschine) und
   * `targetId` (Bufferstock) in Domänen-Richtung.
   *
   * @param position Zu buchende Position (article_id und amount)
   * @param params Buchungskontext, Adapter und stabile client_ids
   * @returns BookingResult; Fehler bei fehlender targetId, fehlendem Maschinenlager oder Speicher-/Update-Fehlschlag
   */
  protected async toBufferstockDisposal(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<BookingResult> {
    const { context, adapters } = params;
    const { booking } = context;
    if (booking.targetId === undefined) {
      return this.failure(
        'Maschine→BufferstockDisposal: targetId fehlt',
      );
    }
    // Das Movement braucht die StockObject-ID des Maschinenlagers, nicht die
    // Maschinen-ID - sonst findet das Backend kein Stock und der Bestand bleibt
    // unverbucht (stiller Drift). Das lokale Update unten nutzt weiter die
    // Maschinen-ID (der MachineAdapter löst selbst auf).
    const machineId = booking.sourceId;
    const stockObjectId =
      await adapters.machine.findStockObjectIdLocal(machineId);
    if (stockObjectId === null) {
      return this.failure(
        `Maschine→BufferstockDisposal: Maschine ${machineId} hat kein Lager (StockObject)`,
      );
    }
    // 1. MachineTrashMovement remote speichern (target = Maschinenlager-StockObject)
    const movement = adapters.movement.build(
      position,
      { ...booking, sourceId: stockObjectId },
      params.clientIds.idFor(position),
    );
    const saved = await adapters.movement.createRemote(movement);
    if (!saved.value) {
      return this.failure(
        'MachineTrashMovement konnte nicht gespeichert werden',
        saved.retryable,
      );
    }
    // 2. Optional in Transaction enqueuen (für Rollback)
    const rollbackFailure = this.enqueueForRollback(position, params);
    if (rollbackFailure) {
      return rollbackFailure;
    }
    // 3. Maschinenbestand lokal reduzieren
    const updatedMachine = await adapters.machine.updatePositionAmountLocal(
      position,
      booking.sourceId,
      -position.amount
    );
    if (!updatedMachine) {
      return this.failure(
        `Maschine ${booking.sourceId} konnte nicht lokal gespeichert werden`
      );
    }
    // 4. Disposal-Eintrag am Bufferstock erhöhen
    const addedDisposal = await adapters.bufferstock.addDisposalLocal(
      booking.targetId,
      position.article_id,
      position.amount
    );
    if (!addedDisposal) {
      return this.failure(
        `Disposal-Eintrag am Bufferstock ${booking.targetId} konnte nicht gespeichert werden`
      );
    }
    return this.success();
  }
}
